import React from 'react';
import {Box, Typography, Container, Grid, alpha, useTheme, Avatar} from '@mui/material';
import {useTranslation} from "react-i18next";
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import StarIcon from '@mui/icons-material/Star';

const TestimonialsSection = () => {
  const {t} = useTranslation();
  const theme = useTheme();

  const testimonials = [
    {
      name: 'David Cohen',
      role: t('Owner, Hummus Bar Tel Aviv'),
      avatar: 'DC',
      rating: 5,
      text: t('QRseller transformed our business. We went from paper menus and long queues to smooth digital ordering in one day. Our revenue increased by 40% in the first month.'),
    },
    {
      name: 'Sarah Levi',
      role: t('Manager, Carmel Market Kiosk'),
      avatar: 'SL',
      rating: 5,
      text: t('Simple, fast, affordable. I was skeptical at first, but the setup took 10 minutes. Now I can focus on cooking instead of taking orders. Best decision we made.'),
    },
    {
      name: 'Michael Rosenberg',
      role: t('Owner, Café Rothschild'),
      avatar: 'MR',
      rating: 5,
      text: t('The analytics feature alone is worth it. I can see exactly what sells and when. Plus, my staff loves not having to handle cash or write down orders.'),
    },
  ];

  return (
    <Box
      sx={{
        py: {xs: 8, md: 12},
        bgcolor: 'background.paper',
        position: 'relative',
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{textAlign: 'center', mb: 8}}>
          <Typography
            variant="overline"
            sx={{
              color: 'primary.main',
              fontWeight: 600,
              letterSpacing: '0.1em',
              fontSize: '0.875rem',
            }}
          >
            {t("TESTIMONIALS")}
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: {xs: '2rem', md: '3rem'},
              fontWeight: 700,
              mt: 1,
              mb: 2,
              color: 'text.primary',
            }}
          >
            {t("Loved by business owners")}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: '1.125rem',
              color: 'text.secondary',
              maxWidth: '700px',
              mx: 'auto',
            }}
          >
            {t("See what real businesses are saying about QRseller")}
          </Typography>
        </Box>

        {/* Testimonials Grid */}
        <Grid container spacing={4}>
          {testimonials.map((testimonial, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Box
                sx={{
                  height: '100%',
                  p: 4,
                  borderRadius: '16px',
                  bgcolor: 'background.default',
                  border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: `0 12px 28px ${alpha(theme.palette.primary.main, 0.15)}`,
                    borderColor: 'primary.main',
                  },
                }}
              >
                {/* Quote Icon */}
                <FormatQuoteIcon
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    fontSize: 48,
                    color: alpha(theme.palette.primary.main, 0.1),
                    transform: 'rotate(180deg)',
                  }}
                />

                {/* Rating */}
                <Box sx={{display: 'flex', gap: 0.5, mb: 2}}>
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <StarIcon
                      key={i}
                      sx={{
                        fontSize: 20,
                        color: '#FFC107',
                      }}
                    />
                  ))}
                </Box>

                {/* Testimonial Text */}
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.primary',
                    lineHeight: 1.7,
                    mb: 3,
                    fontStyle: 'italic',
                  }}
                >
                  "{testimonial.text}"
                </Typography>

                {/* Author Info */}
                <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: 'primary.main',
                      fontWeight: 600,
                    }}
                  >
                    {testimonial.avatar}
                  </Avatar>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                      }}
                    >
                      {testimonial.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {testimonial.role}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Social Proof Stats */}
        <Box
          sx={{
            mt: 8,
            p: 5,
            borderRadius: '16px',
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        >
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={3}>
              <Box sx={{textAlign: {xs: 'center', md: 'left'}}}>
                <Typography variant="h3" sx={{fontWeight: 800, color: 'primary.main', mb: 0.5}}>
                  4.9
                </Typography>
                <Box sx={{display: 'flex', gap: 0.5, justifyContent: {xs: 'center', md: 'flex-start'}, mb: 1}}>
                  {[...Array(5)].map((_, i) => (
                    <StarIcon key={i} sx={{fontSize: 20, color: '#FFC107'}} />
                  ))}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t("Average rating")}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{textAlign: 'center'}}>
                <Typography variant="h3" sx={{fontWeight: 800, color: 'primary.main', mb: 0.5}}>
                  100+
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("Happy businesses")}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{textAlign: 'center'}}>
                <Typography variant="h3" sx={{fontWeight: 800, color: 'primary.main', mb: 0.5}}>
                  10K+
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("Orders processed")}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{textAlign: {xs: 'center', md: 'right'}}}>
                <Typography variant="h3" sx={{fontWeight: 800, color: 'primary.main', mb: 0.5}}>
                  98%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("Customer satisfaction")}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default TestimonialsSection;
